import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

const MahilaMandiDealsNearby = () => {
  const navigate = useNavigate();
  
  const [allProducts, setAllProducts] = useState([]);
  const [nearbyProducts, setNearbyProducts] = useState({});
  const [locationFetched, setLocationFetched] = useState(false);

  useEffect(() => {
    const fallbackProducts = [
      {
        _id: '1',
        title: 'Homemade Mango Pickle',
        brief: 'Spicy and tangy mango pickle crafted by local women artisans.',
        category: 'Pickles & Preserves',
        latitude: 19.0759,
        longitude: 72.8772,
        image: 'pickle',
      },
      {
        _id: '2',
        title: 'Handmade Lavender Soap',
        brief: 'Natural lavender-infused soap for gentle skincare.',
        category: 'Handmade Soaps',
        latitude: 18.5204,
        longitude: 73.8567,
        image: 'soap',
      },
      {
        _id: '3',
        title: 'Artisan Oatmeal Cookies',
        brief: 'Freshly baked, wholesome oatmeal cookies with love.',
        category: 'Baked Goods',
        latitude: 28.6139,
        longitude: 77.2090,
        image: 'cookies',
      },
      {
        _id: '4',
        title: 'Embroidered Cotton Handkerchiefs',
        brief: 'Delicate hand-embroidered handkerchiefs from skilled women weavers.',
        category: 'Textiles',
        latitude: 26.9124,
        longitude: 75.7873,
        image: 'handkerchief',
      },
      {
        _id: '5',
        title: 'Handmade Herbal Tea Blend',
        brief: 'Organic herbal tea blended by women cooperatives.',
        category: 'Herbal Teas',
        latitude: 22.5726,
        longitude: 88.3639,
        image: 'tea',
      },
      {
        _id: '6',
        title: 'Clay Pottery Vase',
        brief: 'Handcrafted terracotta vase from rural women potters.',
        category: 'Pottery',
        latitude: 13.0827,
        longitude: 80.2707,
        image: 'pottery',
      },
    ];

    const fetchProducts = async () => {
      try {
        const res = await axios.get("/api/home-products");
        setAllProducts(res.data?.length ? res.data : fallbackProducts);
      } catch {
        setAllProducts(fallbackProducts);
      }
    };

    fetchProducts();
  }, []);

  const getDistanceInKm = (lat1, lon1, lat2, lon2) => {
    const R = 6371;
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLon = (lon2 - lon1) * Math.PI / 180;
    const a = 
      Math.sin(dLat / 2) ** 2 +
      Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
      Math.sin(dLon / 2) ** 2;
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
  };

  const handleNearbyProducts = () => {
    if (!navigator.geolocation) {
      alert("Geolocation not supported by your browser");
      return;
    }

    navigator.geolocation.getCurrentPosition((position) => {
      const userLat = position.coords.latitude;
      const userLon = position.coords.longitude;

      const ranges = {
        'Within 2 Km': [],
        'Within 5 Km': [],
        'Within 10 Km': [],
        'Within 25 Km': [],
        'More than 25 Km': []
      };

      allProducts.forEach(product => {
        if (product.latitude && product.longitude) {
          const distance = getDistanceInKm(userLat, userLon, product.latitude, product.longitude);
          const productWithDistance = { ...product, distance };

          if (distance <= 2) ranges['Within 2 Km'].push(productWithDistance);
          else if (distance <= 5) ranges['Within 5 Km'].push(productWithDistance);
          else if (distance <= 10) ranges['Within 10 Km'].push(productWithDistance);
          else if (distance <= 25) ranges['Within 25 Km'].push(productWithDistance);
          else ranges['More than 25 Km'].push(productWithDistance);
        }
      });

      setNearbyProducts(ranges);
      setLocationFetched(true);
    }, () => {
      alert("Location access denied or failed");
    });
  };

  return (
    <div className="p-4 sm:p-6 bg-black text-white border border-green-500 min-h-screen">
      <h2 className="text-lg sm:text-xl lg:text-2xl font-bold text-yellow-200 mb-4 text-center sm:text-left">
        Find Women-Led Home Products Near You (if Available Now)
      </h2>

      <div className="mb-6 text-center sm:text-left">
        <button
          onClick={handleNearbyProducts}
          className="w-full sm:w-auto px-4 py-2 bg-green-600 text-white rounded-full hover:bg-green-700 transition"
        >
          Refresh Nearby Deals
        </button>
      </div>

      {locationFetched && Object.keys(nearbyProducts).map(range => (
        nearbyProducts[range].length > 0 && (
          <div key={range} className="mb-6 sm:mb-8">
            <h3 className="text-lg sm:text-xl font-semibold mb-3 text-yellow-200">{range}</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
              {nearbyProducts[range].map(product => (
                <div key={product._id} className="bg-black border border-green-400 shadow-md p-3 sm:p-4 rounded-xl flex flex-col">
                  <img 
                    src={`/api/downloads/home-products/${product.image}.jpg`}
                    alt={product.title}
                    className="w-full h-32 sm:h-40 object-cover rounded"
                  />
                  <h4 className="mt-2 text-base sm:text-lg font-bold text-gray-200">{product.title}</h4>
                  <p className="text-xs sm:text-sm text-gray-300">{product.brief}</p>
                  <p className="text-sm mt-1 text-white">~{product.distance.toFixed(1)} km away</p>
                  <button 
                    onClick={() => navigate(`/women-product/${product._id}`)}
                    className="mt-3 w-full sm:w-auto px-4 py-2 bg-green-500 text-white rounded-full text-sm hover:bg-green-600 transition"
                  >
                    View Details
                  </button>
                </div>
              ))}
            </div>
          </div>
        )
      ))}

      {!locationFetched && (
        <p className="text-center text-gray-300 mt-8">Click "Refresh Nearby Deals" to find products based on your location.</p>
      )}
    </div>
  );
};

export default MahilaMandiDealsNearby;